import React, { useState } from "react";
import {
  Container,
  Box,
  Heading,
  Flex,
  Center,
  Text,
  FormLabel,
  Button,
  List,
  ListItem,
  ListIcon,
  TableContainer,
  Table,
  Thead,
  Tr,
  Td,
  Tbody,
  Th,
  CSSReset,
} from "@chakra-ui/react";
import {
  CheckCircleIcon,
  NotAllowedIcon,
  ViewIcon,
  DeleteIcon,
  SmallCloseIcon,
} from "@chakra-ui/icons";
import { TbListDetails } from "react-icons/tb";
import { Query, Mutation, MutationFn, QueryResult } from "react-apollo";
import gql from "graphql-tag";
import UserSelect from "../AssignFavoritesPage/UserSelect";
import { BiLockOpen } from "react-icons/bi";
import { useToast } from "@chakra-ui/react";
import { capitalizeAllWords } from "../../lib/utils";
import { RouteComponentProps } from "react-router";

const GET_OBSERVATORY_BUNDLE = gql`
  query AdminGetObservatoryBundle($id: String!) {
    adminGetObservatoryBundle(id: $id) {
      id
      name
      roles
      isActive
    }
  }
`;

const CREATE_USER_OBSERVATORY_BUNDLE = gql`
  mutation CreateUserObservatoryBundle(
    $userId: String!
    $observatoryBundleId: String!
  ) {
    createUserObservatoryBundle(
      userId: $userId
      observatoryBundleId: $observatoryBundleId
    ) {
      userObservatoryBundle {
        id
      }
      success
      message
    }
  }
`;

const DELETE_USER_OBSERVATORY_BUNDLE = gql`
  mutation DeleteUserObservatoryBundle(
    $userId: String!
    $observatoryBundleId: String!
  ) {
    deleteUserObservatoryBundle(
      userId: $userId
      observatoryBundleId: $observatoryBundleId
    ) {
      userObservatoryBundle {
        id
      }
      message
      success
    }
  }
`;

const GET_OBSERVATORY_BUNDLE_USERS = gql`
  query AdminGetObservatoryBundleUsers($observatoryBundleId: String!) {
    adminGetObservatoryBundleUsers(observatoryBundleId: $observatoryBundleId) {
      id
      email
      name
      organization
    }
  }
`;

const TOAST_OPTIONS = { duration: 9000, isClosable: true };

interface ObservatoryBundleUserRowProps {
  observatoryBundleId: string;
  observatoryBundleName: string;
  user: User;
}

const ObservatoryBundleUserRow = (props: ObservatoryBundleUserRowProps) => {
  const toast = useToast(TOAST_OPTIONS);
  const { observatoryBundleId, observatoryBundleName, user } = props;

  return (
    <Tr>
      <Td>{user.organization}</Td>
      <Td>{capitalizeAllWords(user.name)}</Td>
      <Td>{user.email}</Td>

      <Mutation<
        DeleteUserObservatoryBundleResult,
        DeleteUserObservatoryBundleVariables
      >
        mutation={DELETE_USER_OBSERVATORY_BUNDLE}
        refetchQueries={[
          {
            query: GET_OBSERVATORY_BUNDLE_USERS,
            variables: {
              observatoryBundleId,
            },
          },
        ]}
      >
        {(deleteUserObservatoryBundle) => {
          return (
            <Td>
              <SmallCloseIcon
                cursor={"pointer"}
                onClick={async () => {
                  const result = await deleteUserObservatoryBundle({
                    variables: {
                      userId: user.id,
                      observatoryBundleId,
                    },
                  });

                  if (!result) {
                    toast({
                      title: "Error",
                      description: "An error occurred while deleting user.",
                      status: "error",
                    });
                  }

                  const data = result?.data;

                  const status = data?.deleteUserObservatoryBundle.success
                    ? "success"
                    : "error";

                  if (data?.deleteUserObservatoryBundle.success) {
                    toast({
                      title: "Observatory Bundle Access",
                      description: `"${user.email} has been removed from "${observatoryBundleName}".`,
                      status,
                    });
                  }
                }}
              />
            </Td>
          );
        }}
      </Mutation>
    </Tr>
  );
};

const ObservatoryBundleAccess = (
  props: RouteComponentProps<{ id: string }>
): JSX.Element => {
  const id = props.match.params.id;
  const [selectedUser, setSelectedUser] = useState<SelectedOption>(null);

  const toast = useToast(TOAST_OPTIONS);

  return (
    <Query<
      AdminGetObservatoryBundleQueryResults,
      AdminGetObservatoryBundleQueryVariables
    >
      query={GET_OBSERVATORY_BUNDLE}
      variables={{ id }}
    >
      {({ data, error }) => {
        if (error) {
          return <div>Error getting observatory bundle.</div>;
        }

        if (!data || !data?.adminGetObservatoryBundle) {
          return <div>Error getting observatory bundle.</div>;
        }

        const { id, name, roles, isActive } = data.adminGetObservatoryBundle;

        return (
          <>
            <CSSReset />
            <Container maxW={"1250px"}>
              <Box>
                <Heading size={"lg"} mb={5}>
                  <ViewIcon w={6} h={6} paddingBottom={1} mr={2} />
                  Observatory Bundle Access: {name}
                </Heading>
                <Box
                  bg="#F7FAFC"
                  border="1px"
                  borderColor="#A0AEC0"
                  borderRadius="5px"
                  p="20px"
                  mb={"8"}
                  display={"flex"}
                >
                  <Box flex={1}>
                    <Heading size={"md"} mb={5}>
                      <Flex>
                        <Center>
                          <TbListDetails />
                          <Text mb={0} ml={2}>
                            Details
                          </Text>
                        </Center>
                      </Flex>
                    </Heading>
                    <Flex>
                      <FormLabel>ID: </FormLabel>
                      <Text>{id}</Text>
                    </Flex>
                    <Flex>
                      <FormLabel>Name: </FormLabel>
                      <Text>{name}</Text>
                    </Flex>
                    <Flex>
                      <FormLabel mb={2}>Is Active: </FormLabel>
                      {isActive ? (
                        <CheckCircleIcon mt={1} />
                      ) : (
                        <NotAllowedIcon mt={1} />
                      )}
                    </Flex>
                    <Box>
                      <FormLabel>
                        Permissioned Roles:
                        <Text fontSize={"xs"}>
                          Users with any of the following roles has permission
                          to access this model.
                        </Text>
                      </FormLabel>
                      <List pl={"10px"} pb={0}>
                        {roles.length ? (
                          roles.map((role, idx) => {
                            return (
                              <ListItem key={idx}>
                                <ListIcon
                                  as={CheckCircleIcon}
                                  color="green.500"
                                />
                                {role}
                              </ListItem>
                            );
                          })
                        ) : (
                          <ListItem>
                            <Text>No roles assigned</Text>
                          </ListItem>
                        )}
                      </List>
                    </Box>
                  </Box>
                  <Box
                    flex={1}
                    borderLeft={"1px solid #A0AEC0"}
                    paddingLeft={"20px"}
                  >
                    <FormLabel>
                      Permissioned Users:
                      <Text fontSize={"xs"}>
                        Users with individual access to this model.
                      </Text>
                    </FormLabel>
                    <Query
                      query={GET_OBSERVATORY_BUNDLE_USERS}
                      variables={{ observatoryBundleId: id }}
                    >
                      {({
                        data,
                        error,
                      }: QueryResult<
                        GetObservatoryBundleUsersQueryResults,
                        GetUserObservatoryBundleQueryVariables
                      >) => {
                        if (error) {
                          return (
                            <p>
                              Error while getting users with individual access
                              to this market array.
                            </p>
                          );
                        }

                        if (!data?.adminGetObservatoryBundleUsers) {
                          return null;
                        }

                        return (
                          <Box maxH="300px" overflow="auto">
                            <TableContainer>
                              <Table size="sm">
                                <Thead>
                                  <Tr>
                                    <Th>Organzation</Th>
                                    <Th>Name</Th>
                                    <Th>Email</Th>
                                    <Th>
                                      <DeleteIcon />
                                    </Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {!data.adminGetObservatoryBundleUsers
                                    ?.length ? (
                                    <Tr>
                                      <Td colSpan={4} textAlign="center">
                                        No Users Assigned
                                      </Td>
                                    </Tr>
                                  ) : (
                                    data.adminGetObservatoryBundleUsers.map(
                                      (user) => (
                                        <ObservatoryBundleUserRow
                                          key={user.id}
                                          user={user}
                                          observatoryBundleId={id}
                                          observatoryBundleName={name}
                                        />
                                      )
                                    )
                                  )}
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </Box>
                        );
                      }}
                    </Query>
                  </Box>
                </Box>
              </Box>
              <Box
                bg="#F7FAFC"
                border="1px"
                borderColor="#A0AEC0"
                borderRadius="5px"
                p="20px"
              >
                <Heading size={"md"} mb={5}>
                  <Flex>
                    <Center>
                      <BiLockOpen />
                      <Text mb={0} ml={2}>
                        Grant Access
                      </Text>
                    </Center>
                  </Flex>
                </Heading>
                <UserSelect
                  onUserChange={setSelectedUser}
                  selectedUser={selectedUser}
                />
                <Mutation<
                  CreateBundleToUserMutationResult,
                  CreateBundleToUserMutationVariables
                >
                  mutation={CREATE_USER_OBSERVATORY_BUNDLE}
                  refetchQueries={[
                    {
                      query: GET_OBSERVATORY_BUNDLE_USERS,
                      variables: {
                        observatoryBundleId: id,
                      },
                    },
                  ]}
                >
                  {(
                    createUserObservatoryBundle: MutationFn<
                      CreateBundleToUserMutationResult,
                      CreateBundleToUserMutationVariables
                    >
                  ) => {
                    return (
                      <Button
                        mt={3}
                        data-cy="allow-model-button"
                        _hover={{ bg: "#004C6E" }}
                        cursor={"pointer"}
                        rightIcon={<ViewIcon />}
                        color="white"
                        border={"none"}
                        bg="#5B646D"
                        onClick={async (e) => {
                          e.preventDefault();

                          if (!selectedUser?.value) {
                            toast({
                              title: "Missing user field.",
                              description:
                                "A user must be selected to allow access to observatory bundle.",
                              status: "error",
                            });

                            return;
                          }

                          const result = await createUserObservatoryBundle({
                            variables: {
                              observatoryBundleId: id,
                              userId: selectedUser.value,
                            },
                          });

                          const data = result?.data;

                          if (data?.createUserObservatoryBundle.success) {
                            toast({
                              title: "Observatory Bundle Access",
                              description: `"${selectedUser.label}" has been granted access to "${name}".`,
                              status: "success",
                            });
                            setSelectedUser(null);
                          } else {
                            toast({
                              title: "Observatory Bundle Access",
                              description:
                                data?.createUserObservatoryBundle?.message ||
                                "Unable to grant user access to this bundle.",
                              status: "error",
                            });
                          }
                        }}
                      >
                        Allow Observatory Bundle Access
                      </Button>
                    );
                  }}
                </Mutation>
              </Box>
            </Container>
          </>
        );
      }}
    </Query>
  );
};

export default ObservatoryBundleAccess;

interface AdminGetObservatoryBundleQueryResults {
  adminGetObservatoryBundle: {
    id: string;
    name: string;
    roles: string[];
    isActive: boolean;
  };
}

interface AdminGetObservatoryBundleQueryVariables {
  id: string;
}

interface SelectOption {
  label: string;
  value: string;
}

type SelectedOption = SelectOption | null;

interface User {
  id: string;
  email: string;
  name: string;
  organization?: string;
}

interface CreateBundleToUserMutationResult {
  createUserObservatoryBundle: {
    userObservatoryBundle: {
      id: string;
    };
    success: boolean;
    message: string;
  };
}

interface CreateBundleToUserMutationVariables {
  observatoryBundleId: string;
  userId: string;
}

interface GetUserObservatoryBundleQueryVariables {
  observatoryBundleId: string;
}

interface GetObservatoryBundleUsersQueryResults {
  adminGetObservatoryBundleUsers: User[];
}

interface DeleteUserObservatoryBundleResult {
  deleteUserObservatoryBundle: {
    userObservatoryBundle: {
      id: string;
    };
    message: string;
    success: boolean;
  };
}

interface DeleteUserObservatoryBundleVariables {
  userId: string;
  observatoryBundleId: string;
}
